<template>
    <div v-if="open" class="hems-pwd-root">
        <div class="hems-pwd-backdrop" @click="onBackdrop"></div>
        <div class="hems-pwd-panel" role="dialog" aria-modal="true" aria-labelledby="hems-pwd-title" @click.stop>
            <div class="hems-pwd-handle"></div>
            <h2 id="hems-pwd-title" class="hems-pwd-title">비밀번호 변경</h2>
            <p class="hems-pwd-hint">현재 비밀번호와 새 비밀번호를 입력해 주세요.</p>

            <form class="hems-pwd-form" @submit.prevent="onSubmit">
                <label class="hems-pwd-field">
                    <span class="hems-pwd-label">현재 비밀번호</span>
                    <input
                        v-model="current"
                        type="password"
                        class="hems-pwd-input"
                        :class="{ 'is-invalid': errors.current }"
                        autocomplete="current-password"
                        maxlength="20"
                        :disabled="saving"
                    />
                    <span v-if="errors.current" class="hems-pwd-err">{{ errors.current }}</span>
                </label>

                <label class="hems-pwd-field">
                    <span class="hems-pwd-label">새 비밀번호</span>
                    <input
                        v-model="newPwd"
                        type="password"
                        class="hems-pwd-input"
                        :class="{ 'is-invalid': errors.newPwd }"
                        autocomplete="new-password"
                        maxlength="20"
                        :disabled="saving"
                    />
                    <span v-if="errors.newPwd" class="hems-pwd-err">{{ errors.newPwd }}</span>
                </label>

                <label class="hems-pwd-field">
                    <span class="hems-pwd-label">새 비밀번호 확인</span>
                    <input
                        v-model="confirmPwd"
                        type="password"
                        class="hems-pwd-input"
                        :class="{ 'is-invalid': errors.confirmPwd }"
                        autocomplete="new-password"
                        maxlength="20"
                        :disabled="saving"
                    />
                    <span v-if="errors.confirmPwd" class="hems-pwd-err">{{ errors.confirmPwd }}</span>
                </label>

                <p class="hems-pwd-policy">
                    영대·영소·숫자·특수문자 중 3종류 이상, 9~20자. 아이디·현재 비밀번호와 달라야 합니다.
                </p>

                <div class="hems-pwd-actions">
                    <button type="button" class="hems-pwd-btn ghost" :disabled="saving" @click="$emit('close')">
                        취소
                    </button>
                    <button type="submit" class="hems-pwd-btn primary" :disabled="saving">
                        <b-spinner v-if="saving" small />
                        <span v-else>저장</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";

/** PC Validator.js `password` 규칙과 동일 */
const PASSWORD_COMPLEXITY =
    /^((?=.*[A-Z])(?=.*[a-z])(?=.*\d)|(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*])|(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])|(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*]))[A-Za-z\d!@#$%^&*()+_]{9,20}$/;

const HAS_KOREAN = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;

export default {
    name: "HemsChangePasswordSheet",
    props: {
        open: { type: Boolean, default: false },
        userId: { type: String, default: "" },
    },
    data() {
        return {
            current: "",
            newPwd: "",
            confirmPwd: "",
            errors: {},
            saving: false,
        };
    },
    watch: {
        open(v) {
            if (v) this.resetForm();
        },
    },
    methods: {
        resetForm() {
            this.current = "";
            this.newPwd = "";
            this.confirmPwd = "";
            this.errors = {};
            this.saving = false;
        },
        onBackdrop() {
            if (!this.saving) this.$emit("close");
        },
        /** PC ChangePwdModal / ruleConsts 와 동일 정책 */
        validate() {
            const errors = {};
            const current = String(this.current || "");
            const newPwd = String(this.newPwd || "");
            const confirmPwd = String(this.confirmPwd || "");
            const userId = String(this.userId || "");

            if (!current) {
                errors.current = "현재 비밀번호를 입력해 주세요.";
            } else if (HAS_KOREAN.test(current)) {
                errors.current = "비밀번호에 한글을 사용할 수 없습니다.";
            } else if (current.length < 9 || current.length > 20) {
                errors.current = "비밀번호는 9~20자로 입력해 주세요.";
            }

            if (!newPwd) {
                errors.newPwd = "새 비밀번호를 입력해 주세요.";
            } else if (!PASSWORD_COMPLEXITY.test(newPwd)) {
                errors.newPwd =
                    "영대문자, 영소문자, 숫자, 특수문자를 3종류 이상으로 조합하고 9~20자리로 설정해야 합니다.";
            } else if (userId && newPwd === userId) {
                errors.newPwd = "비밀번호는 아이디와 동일하지 않아야 합니다.";
            } else if (newPwd === current) {
                errors.newPwd = "현재 비밀번호와 새 비밀번호를 다르게 해 주세요.";
            }

            if (!confirmPwd) {
                errors.confirmPwd = "새 비밀번호 확인을 입력해 주세요.";
            } else if (confirmPwd !== newPwd) {
                errors.confirmPwd = "새 비밀번호 확인이 일치하지 않습니다.";
            }

            this.errors = errors;
            return Object.keys(errors).length === 0;
        },
        toastSuccess(message) {
            if (this.$toast?.success) this.$toast.success(message);
            else if (this.$bvToast) {
                this.$bvToast.toast(message, { variant: "success", solid: true, autoHideDelay: 3000 });
            }
        },
        toastError(message) {
            if (this.$toast?.error) this.$toast.error(message);
            else if (this.$bvToast) {
                this.$bvToast.toast(message, { variant: "danger", solid: true, autoHideDelay: 4000 });
            }
        },
        async onSubmit() {
            if (this.saving || !this.validate()) return;
            this.saving = true;
            try {
                const result = await backEndApi.user.changeUserPwd({
                    userId: this.userId,
                    current: this.current,
                    newPwd: this.newPwd,
                });
                if (result.status === 200) {
                    this.toastSuccess("비밀번호가 변경되었습니다.");
                    this.$emit("success");
                    this.$emit("close");
                } else {
                    this.toastError(result?.data?.detail || "비밀번호를 변경하지 못했습니다.");
                }
            } catch (err) {
                const detail = err?.response?.data?.detail || err?.message || "비밀번호를 변경하지 못했습니다.";
                this.toastError(detail);
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.hems-pwd-root {
    position: fixed;
    inset: 0;
    z-index: 220;
}
.hems-pwd-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(20, 30, 60, 0.35);
}
.hems-pwd-panel {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    max-height: min(92vh, 640px);
    max-height: min(92dvh, 640px);
    padding: 12px 18px calc(18px + env(safe-area-inset-bottom, 0px));
    border-radius: 18px 18px 0 0;
    background: #fff;
    box-shadow: 0 -8px 32px rgba(20, 30, 60, 0.12);
    overflow: auto;
    -webkit-overflow-scrolling: touch;
}
.hems-pwd-handle {
    width: 36px;
    height: 4px;
    margin: 0 auto 14px;
    border-radius: 99px;
    background: #e6e9ef;
    flex-shrink: 0;
}
.hems-pwd-title {
    margin: 0 0 6px;
    font-size: 1.19rem;
    font-weight: 800;
    color: #1a1f27;
}
.hems-pwd-hint {
    margin: 0 0 16px;
    font-size: 0.88rem;
    color: #6b7484;
    line-height: 1.4;
}
.hems-pwd-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.hems-pwd-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.hems-pwd-label {
    font-size: 0.88rem;
    font-weight: 700;
    color: #4b5363;
}
.hems-pwd-input {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #e2e6ed;
    border-radius: 10px;
    font-size: 1rem;
    color: #1a1f27;
    background: #fff;
}
.hems-pwd-input:focus {
    outline: none;
    border-color: #4b6cb7;
    box-shadow: 0 0 0 3px rgba(75, 108, 183, 0.15);
}
.hems-pwd-input.is-invalid {
    border-color: #c0392b;
}
.hems-pwd-input:disabled {
    opacity: 0.65;
    background: #f7f8fa;
}
.hems-pwd-err {
    font-size: 0.81rem;
    color: #c0392b;
    line-height: 1.35;
}
.hems-pwd-policy {
    margin: 0;
    font-size: 0.81rem;
    color: #6b7484;
    line-height: 1.4;
}
.hems-pwd-actions {
    display: flex;
    gap: 10px;
    margin-top: 4px;
}
.hems-pwd-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid #e2e6ed;
    font-size: 1.06rem;
    font-weight: 700;
    cursor: pointer;
    background: #fff;
    color: #1a1f27;
}
.hems-pwd-btn.primary {
    border-color: #1a1f27;
    background: #1a1f27;
    color: #fff;
}
.hems-pwd-btn.ghost {
    background: #fff;
    color: #4b5363;
}
.hems-pwd-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
</style>
